/* eslint-disable max-statements */
import { proxy } from "valtio/vanilla";
import parseCookie from "../utils/helpers/parse-cookie";
import { reducerManager } from "../reducer-manager";
import axios from "axios";
import { HTTP_CODES } from "../constants/app-constants";

export const tokenState = proxy({
    isTokenValid: true
});

const bifrostConfig = {
    clientId: process.env.BIFROST_CLIENT_ID,
    redirectUri: process.env.BIFROST_REDIRECT_URI
};

const bifrostAuthApi = axios.create({
    baseURL: process.env.BIFROST_AUTH_SERVICE_BASE_URL
});

const appAuthApi = axios.create({
    baseURL: typeof window !== "undefined" ? window.location.origin : "",
    withCredentials: true
});

export const getUserState = () => {
    const store = reducerManager.getStore();
    return store.getState().user || null;
};

// Double-guard to prevent race conditions
let refreshTokenPromise = null;
let isRefreshing = false;

const handleAuthFailure = async () => {
    tokenState.isTokenValid = false;
    reducerManager.getDispatch()({
        type: "configuration/MARK_UNAUTHORIZED"
    });
};

export const refreshToken = async (refreshTokenValue, sessionId) => {
    const deviceId = parseCookie("bifrost_device_id") || parseCookie("statsigStableId");

    const response = await bifrostAuthApi.post(
        "/oauth2/token",
        {
            grant_type: "refresh_token",
            refresh_token: refreshTokenValue,
            client_id: bifrostConfig.clientId,
            session_id: sessionId,
            redirect_uri: bifrostConfig.redirectUri
        },
        {
            headers: {
                "Content-Type": "application/json",
                "x-device-id": deviceId
            }
        }
    );

    return response;
};

const configureBifrostAuth = async (tokenData) => {
    const {
        bifrostToken,
        refreshToken: refreshTokenValue,
        sessionId,
        expiresIn,
        refreshTokenExpiryIn
    } = tokenData;

    const response = await appAuthApi.post(
        "/auth/configure",
        {
            bifrost_token: bifrostToken,
            refresh_token: refreshTokenValue,
            session_id: sessionId,
            expires_in: expiresIn,
            refresh_token_expiry_in: refreshTokenExpiryIn
        },
        {
            headers: {
                "Content-Type": "application/json"
            }
        }
    );

    return response;
};

export const handleTokenRefresh = (axiosInstance, options = {}) => {
    const defaultOptions = {
        shouldRefresh: (error) => {
            const url = error?.config?.url || "";
            const skipPatterns = ["/oauth2/token", "/auth/configure"];
            return !skipPatterns.some((pattern) => url.includes(pattern));
        },
        ...options
    };

    const interceptorId = axiosInstance.interceptors.response.use(
        (response) => response,
        // eslint-disable-next-line complexity
        async (error) => {
            const originalRequest = error?.config;

            // Guard: must have config and be a 401, and not already retried
            if (
                !originalRequest ||
                error?.response?.status !== HTTP_CODES.UNAUTHORIZED ||
                originalRequest._retry
            ) {
                return Promise.reject(error);
            }

            // Check if this URL should be skipped from refresh
            if (!defaultOptions.shouldRefresh(error)) {
                return Promise.reject(error);
            }

            // Mark as retried to prevent infinite loops
            originalRequest._retry = true;

            // Get current user state
            const userState = getUserState();
            const currentRefreshToken = userState?.refreshToken;
            const currentSessionId = userState?.sessionId;
            const currentAccessToken = userState?.secureToken;

            if (!currentRefreshToken) {
                await handleAuthFailure();
                return Promise.reject(error);
            }

            // Check if the request was made with a stale token
            // If the current token in Redux is different from what the request used,
            // it means we already refreshed - just retry with the new token
            const requestToken = originalRequest.headers?.Authorization?.replace?.("Bearer ", "") || "";
            const isTokenStale = currentAccessToken && requestToken && requestToken !== currentAccessToken;

            if (isTokenStale) {
                // Token was already refreshed by another request - just retry with the new token
                if (typeof originalRequest.headers.set === "function") {
                    originalRequest.headers.set("Authorization", `Bearer ${currentAccessToken}`);
                } else {
                    originalRequest.headers.Authorization = `Bearer ${currentAccessToken}`;
                }
                return axios(originalRequest);
            }

            // Use both promise and flag to prevent race conditions
            if (!refreshTokenPromise && !isRefreshing) {
                isRefreshing = true;

                refreshTokenPromise = refreshToken(currentRefreshToken, currentSessionId)
                    .then(async (refreshResponse) => {
                        const data = refreshResponse?.data?.data || {};

                        const newAccessToken = data.access_token;
                        const newRefreshToken = data.refresh_token;
                        const newSessionId = data.session_id;

                        if (!newAccessToken) {
                            throw new Error("No new access token received");
                        }

                        // Configure bifrost auth (sets cookies)
                        await configureBifrostAuth({
                            bifrostToken: newAccessToken,
                            refreshToken: newRefreshToken,
                            sessionId: newSessionId,
                            expiresIn: data.expires_in,
                            refreshTokenExpiryIn: data.refresh_token_expiry_in
                        });

                        // Update Redux store
                        reducerManager.getDispatch()({
                            type: "configuration/SET_BIFROST_TOKENS",
                            secureToken: newAccessToken,
                            refreshToken: newRefreshToken,
                            sessionId: newSessionId
                        });

                        tokenState.isTokenValid = true;

                        return newAccessToken;
                    })
                    .catch(async (refreshError) => {
                        await handleAuthFailure();
                        throw refreshError;
                    })
                    .finally(() => {
                        // Reset both the promise and flag once the refresh process is completed
                        refreshTokenPromise = null;
                        isRefreshing = false;
                    });
            }

            try {
                // Wait for the refresh token promise (either newly created or existing)
                const newAccessToken = await refreshTokenPromise;

                // Update the original request headers with the new token
                if (newAccessToken && originalRequest.headers) {
                    // Use .set() if available (AxiosHeaders), otherwise direct assignment
                    if (typeof originalRequest.headers.set === "function") {
                        originalRequest.headers.set("Authorization", `Bearer ${newAccessToken}`);
                    } else {
                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    }
                }

                // Retry the original request with the updated token
                return axios(originalRequest);
            } catch (refreshError) {
                await handleAuthFailure();
                return Promise.reject(refreshError);
            }
        }
    );

    return {
        remove: () => {
            axiosInstance.interceptors.response.eject(interceptorId);
        }
    };
};

// Backward-compatible export name
const setupAuthRefreshInterceptor = (axiosInstance, options = {}) => {
    return handleTokenRefresh(axiosInstance, options);
};

export {
    setupAuthRefreshInterceptor,
    configureBifrostAuth
};

export default setupAuthRefreshInterceptor;
